import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { DeleteDialog } from 'ui/DeleteDialog';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { getCountryListRequest } from 'redux/reducers/country/reducer';
import { getCountryListData } from 'redux/reducers/country/selectors';
import {
	deleteSupportedPostalOperatorRequest,
	getSupportedPostalOperatorListRequest,
} from 'redux/reducers/supportedPostalOperators/reducer';
import {
	getSupportedPostalOperatorList,
	getSupportedPostalOperatorListLoading,
} from 'redux/reducers/supportedPostalOperators/selectors';
import { TSupportedPostalOperator } from 'redux/reducers/supportedPostalOperators/types';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { SupportedPostalOperatorDialog } from './SupportedPostalOperatorDialog';

const EDIT_ACTION = 'Edit';
const DELETE_ACTION = 'Delete';

export const SupportedPostalOperators: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [operatorToEdit, setOperatorToEdit] = useState<TSupportedPostalOperator | null>(null);
	const [idToDelete, setIdToDelete] = useState<number | null>(null);

	const list = useSelector(getSupportedPostalOperatorList);
	const listLoading = useSelector(getSupportedPostalOperatorListLoading);
	const countries = useSelector(getCountryListData);

	/** The dialog picks a country out of this list, and the table resolves the country id from it. */
	useEffect(() => {
		if (!countries) dispatch(getCountryListRequest());
	}, [countries, dispatch]);

	/** Keeps only the visibility flags, so the columns stay free of the derived render callbacks. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TSupportedPostalOperator>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const loadList = useCallback(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			getSupportedPostalOperatorListRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	const handleAddNew = useCallback(() => {
		setOperatorToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === EDIT_ACTION) {
				setOperatorToEdit(list?.data?.find((item) => item.id === id) ?? null);
				setDialogOpen(true);
			}
			if (action === DELETE_ACTION) {
				setIdToDelete(id);
			}
		},
		[list],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setOperatorToEdit(null);
	}, []);

	const handleDeleteClose = useCallback(() => {
		setIdToDelete(null);
	}, []);

	const handleDeleteConfirm = useCallback(() => {
		if (idToDelete !== null) {
			dispatch(deleteSupportedPostalOperatorRequest({ id: idToDelete }));
		}
		setIdToDelete(null);
	}, [dispatch, idToDelete]);

	return (
		<>
			<SupportedPostalOperatorDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				operatorToEdit={operatorToEdit}
			/>
			<DeleteDialog
				open={idToDelete !== null}
				setClose={handleDeleteClose}
				handleDelete={handleDeleteConfirm}
				text="Are you sure you want to delete this supported postal operator?"
			/>
			<EasTableWithFilter<TSupportedPostalOperator>
				tableStructure={currentTableStructure}
				items={list}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / <b>Supported Postal Operators</b>
					</Typography>
					<Button variant="contained" color="primary" size="small" onClick={handleAddNew}>
						Add New Postal Operator
					</Button>
				</Box>
			</EasTableWithFilter>
		</>
	);
};
